<template>
  <div v-if="lesson" class="page stack">
    <!-- 2026-10-02: this page lost the dictation mic on Matt's phone (iOS put
         every text box into card-autofill mode). Fourteen deploys of bisecting
         pinned it to ONE word, "pan" — PAN is the card-industry name for a
         card number — first in the text near the boxes ("pan-seared"), then,
         once that was gone, in the chat box's own id ("ask-before-pan-sauce";
         iOS scans ids and names too, split on hyphens). Whole word only:
         "pantry" is fine. So the course says skillet, tray and tin
         (lessons.test.mjs keeps it that way), and no element id or name is
         ever built from a lesson id. -->
    <section>
      <p class="eyebrow">Lesson {{ lesson.number }} · {{ lesson.technique }}</p>
      <h1>{{ lesson.title }}</h1>
      <p class="dish">{{ lesson.dish }} <span class="muted">with {{ lesson.side.toLowerCase() }}</span></p>
      <p class="muted">{{ lesson.why }}</p>
      <p class="muted small">Builds on: {{ lesson.buildsOn }} · {{ formatMinutes(lesson.minutes) }}<span v-if="lesson.weekend"> · needs a weekend afternoon</span></p>
      <p v-if="lesson.nightBefore" class="night-before"><strong>The night before:</strong> {{ lesson.nightBefore }}</p>
    </section>

    <section class="card">
      <p class="eyebrow">When</p>
      <!-- The night comes off Meal Hat's schedule itself (loadLessonNights);
           the course's own note is only a fallback when the schedule could
           not be read, and then the card says so. "Not on a night" is said
           only after actually looking. -->
      <p v-if="checking" class="muted">Checking Meal Hat’s schedule…</p>
      <p v-else-if="night && night === today"><strong>Tonight</strong> · on Meal Hat’s schedule.</p>
      <p v-else-if="night && nights"><strong>{{ shortDate(night) }}</strong> · on Meal Hat’s schedule.</p>
      <p v-else-if="night"><strong>{{ shortDate(night) }}</strong> · planned. Couldn’t reach Meal Hat’s schedule just now to check.</p>
      <p v-else-if="nights" class="muted">Not on a night yet. Planning happens by the week.</p>
      <p v-else class="muted">Couldn’t check Meal Hat’s schedule just now. Try again in a moment.</p>
      <router-link to="/week" class="btn btn-sm">The week: nights, order, groceries</router-link>
    </section>

    <section class="card">
      <p class="eyebrow">This lesson asks for</p>
      <ul class="plain-list">
        <li v-for="item in lesson.order" :key="item.creameryId">
          <a :href="itemUrl(item.creameryId, item.name)" target="_blank" rel="noopener">{{ item.name }}</a>
          <span class="muted"> · {{ item.qty }} · from the creamery</span>
          <span v-if="item.note" class="muted note">{{ item.note }}</span>
        </li>
        <li v-for="ing in lesson.grocery" :key="ing.name">{{ ing.name }}<span v-if="ing.qty" class="muted"> · {{ ing.qty }}</span><span v-if="ing.pantry" class="muted"> · pantry</span></li>
      </ul>
      <p class="muted small">The fridge check is on the week page, across every lesson at once.</p>
    </section>

    <section class="card">
      <p class="eyebrow">You’ll need</p>
      <ul class="plain-list"><li v-for="tool in lesson.equipment" :key="tool">{{ tool }}</li></ul>
      <p class="muted small">Based on {{ lesson.source.name }}. <a :href="lesson.source.url" target="_blank" rel="noopener">The original</a>.</p>
    </section>


    <div class="row">
      <router-link :to="`/cook/${lesson.id}`" class="btn btn-primary btn-lg">It’s time to cook</router-link>
      <router-link v-if="entry.cookedAt && !entry.debrief" :to="`/debrief/${lesson.id}`" class="btn btn-lg">We ate. Talk.</router-link>
    </div>
    <p v-if="entry.debrief?.summary" class="muted">Cooked {{ shortDate(entry.cookedAt) }}. {{ entry.debrief.summary.verdict }}</p>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { formatMinutes } from '../curriculum/lessons.js';
import { useRecipe } from '../utils/recipes.js';
import { loadProgress, shortDate, todayISO } from '../utils/course.js';
import { loadLessonNights } from '../utils/mealHatSchedule.js';
import { itemUrl } from '../utils/creamery.js';
import { setActivity, setWhere } from '../state.js';

export default {
  name: 'LessonScreen',
  props: { id: { type: String, required: true }, hat: { type: String, default: null } },
  setup (props) {
    // The household's own copy (utils/recipes.js): the teacher may have revised it.
    const lesson = useRecipe(computed(() => props.hat), computed(() => props.id));
    const entry = ref({});
    // lesson id → night, off Meal Hat's schedule; null when it could not be read.
    const nights = ref(null);
    const checking = ref(true);
    const today = todayISO();
    setActivity('lesson');
    watch(() => props.id, (id) => setWhere({ screen: 'lesson', lessonId: id, phase: 'before', stepIndex: 0 }), { immediate: true });

    // The schedule wins; the course's note stands in only when it was unreadable.
    const night = computed(() => (nights.value
      ? nights.value[lesson.value?.id] || null
      : entry.value.plannedFor || null));

    async function load () {
      checking.value = true;
      if (!props.hat || !lesson.value) {
        entry.value = {};
        nights.value = null;
        checking.value = false;
        return;
      }
      const [progress, scheduled] = await Promise.all([loadProgress(props.hat), loadLessonNights(props.hat)]);
      entry.value = progress[lesson.value.id] || {};
      nights.value = scheduled;
      checking.value = false;
    }
    watch(() => [props.hat, props.id], load, { immediate: true });

    return { lesson, entry, nights, night, checking, today, formatMinutes, itemUrl, shortDate };
  }
};
</script>

<style scoped>
.dish { font-size: 1.15rem; font-weight: 600; }
.small { font-size: 0.85rem; }
.night-before { background: var(--accent-soft); border-radius: var(--radius); padding: 0.6rem 0.9rem; }
.note { display: block; font-style: italic; }
.plain-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
</style>
